import { Injectable } from '@angular/core'
import { AhuService } from '@app/ahu/state/ahu.service'
import { ProjectsService } from '@app/project/state/projects.service'
import { concat, Observable, of, switchMap, toArray } from 'rxjs'
import { AhuQuery } from '@app/ahu/state/ahu.query'
import { ProjectsQuery } from '@app/project/state/projects.query'
import { ConfirmService, Selection } from '@app/shared/services/confirm.service'
import { catchError, map } from 'rxjs/operators'

/**
 * Service which provided general-level access to unsaved changes in either projects or units. The purpose of this service
 * is to give the settings dropdown in the app header access to the unsaved state of entities as we don't want to, for example,
 * change measurement units if the user is currently editing a unit.
 */
@Injectable()
export class UnsavedChangesService {
  constructor(
    private ahuService: AhuService,
    private ahuQuery: AhuQuery,
    private projectService: ProjectsService,
    private projectQuery: ProjectsQuery,
    private confirmService: ConfirmService,
  ) {}

  /**
   * Returns a true/false observable which ensures that any unsaved changes in projects or units are saved. Returns TRUE if the user
   * resolves all outstanding changes, otherwise FALSE.
   */
  public checkAndRequestActionForUnsavedChanges(): Observable<boolean> {
    const confirmations: Observable<boolean>[] = []

    if (this.projectQuery.isModified) {
      confirmations.push(this.createProjectConfirmation())
    }
    if (this.ahuQuery.isModified) {
      confirmations.push(this.createAhuConfirmation())
    }

    if (confirmations.length === 0) {
      return of(true)
    }

    // Concatenate the confirmations in case there are multiple entities with unsaved changes
    return concat(...confirmations)
      .pipe(toArray())
      .pipe(
        map(values => {
          return values.indexOf(false) === -1
        }),
      )
  }

  private createAhuConfirmation(): Observable<boolean> {
    return this.createConfirmation(
      this.ahuQuery,
      () => this.ahuService.discard(this.ahuQuery.getActive()?.Id, true),
      () => {
        const active = this.ahuQuery.getActive()
        return this.ahuService.save(active?.Id, active?.ProjectId, true)
      },
    )
  }

  private createProjectConfirmation(): Observable<boolean> {
    return this.createConfirmation(
      this.projectQuery,
      () => this.projectService.resetProject(this.projectQuery.getActive()?.Id, true),
      () => {
        const active = this.projectQuery.getActive()
        return this.projectService.saveProject(active?.Id, true)
      },
    )
  }

  /**
   * Create a confirmation dialog with callbacks for the specified entity
   * @param query Entity store, used to check valid/modifed state
   * @param onDiscard Called when user chooses discard
   * @param onSave Called when user chooses save
   */
  private createConfirmation(
    query: ProjectsQuery | AhuQuery,
    onDiscard: () => Observable<unknown>,
    onSave: () => Observable<unknown>,
  ): Observable<boolean> {
    return this.confirmService.confirm('UI.SaveConfirmTitle', this.getConfirmationText(query.isValid), query.isValid).pipe(
      switchMap(({ selection }) => {
        if (selection === Selection.Discard) {
          return onDiscard().pipe(
            catchError(() => of(false)),
            map(() => true),
          )
        }

        if (selection === Selection.Save) {
          return onSave().pipe(
            catchError(() => of(false)),
            map(() => true),
          )
        }

        return of(false)
      }),
    )
  }

  private getConfirmationText(valid: boolean): string {
    return valid ? 'UI.SaveConfirm' : 'UI.Message.SaveConfirmNotValid'
  }
}
